/**
 * Sales Target & Break-Even Advisor.
 *
 * Plain-English "how much sales do I need?" tool. Backed by
 * POST /api/portal/breakeven/compute which pulls trailing 3-month P&L
 * from Prosaic/Xero, classifies fixed vs variable, and returns every
 * headline number a small-business owner needs.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Target, Loader2, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });
const nz = (v) => (v == null ? "—" : NZD.format(v));
const pct = (v) => (v == null ? "—" : `${Math.round(Number(v) * 1000) / 10}%`);

const PERIOD_OPTIONS = [
    { value: "next_month",    label: "Next month" },
    { value: "next_quarter",  label: "Next quarter" },
    { value: "financial_year", label: "Full financial year" },
    { value: "ytd",           label: "Year to date" },
    { value: "remaining_fy",  label: "Remaining financial year" },
    { value: "custom",        label: "Custom range" },
];

const CONFIDENCE_STYLES = {
    high:   { cls: "border-emerald-200 bg-emerald-50 text-emerald-900", label: "High confidence" },
    medium: { cls: "border-amber-200 bg-amber-50 text-amber-900",       label: "Medium confidence" },
    low:    { cls: "border-red-200 bg-red-50 text-red-900",             label: "Low confidence" },
};

export default function PortalBreakEven() {
    const { activeClientId } = useAuth();
    const [form, setForm] = useState({
        period: "next_month",
        custom_from: "",
        custom_to: "",
        include_owner_wage: false,
        owner_wage_amount: "",
        target_profit: "",
        forecast_sales: "",
        avg_unit_value: "",
        unit_label: "customer",
    });
    const [answer, setAnswer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showMath, setShowMath] = useState(false);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const compute = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const { data } = await api.post("/portal/breakeven/compute", {
                client_id: activeClientId || undefined,
                period: form.period,
                custom_from: form.custom_from || null,
                custom_to: form.custom_to || null,
                include_owner_wage: !!form.include_owner_wage,
                owner_wage_amount: Number(form.owner_wage_amount || 0),
                target_profit: Number(form.target_profit || 0),
                forecast_sales: form.forecast_sales === "" ? null : Number(form.forecast_sales),
                avg_unit_value: form.avg_unit_value === "" ? null : Number(form.avg_unit_value),
                unit_label: form.unit_label || "customer",
            });
            setAnswer(data);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || "Failed to compute");
        } finally { setLoading(false); }
    }, [form, activeClientId]);

    useEffect(() => { compute(); /* run once on mount */ /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const t = answer?.totals || {};
    const c = CONFIDENCE_STYLES[answer?.confidence] || CONFIDENCE_STYLES.medium;

    return (
        <div className="space-y-5 pb-10" data-testid="mq-break-even-page">
            <Link to="/portal" className="inline-flex items-center gap-1.5 text-xs text-[#5E636E] hover:text-[#0F1115]">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to home
            </Link>

            <div className="rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-600 text-white p-5 sm:p-6">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-medium">
                    <Target className="h-3.5 w-3.5" /> Uses your data
                </div>
                <h1 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                    Sales target &amp; break-even
                </h1>
                <p className="mt-2 text-[14px] text-white/85 leading-relaxed max-w-xl">
                    How much do you need to sell to cover your costs? Pick a period and we'll do the maths from your accounts.
                </p>
            </div>

            {/* Controls */}
            <div className="rounded-2xl border border-[#EEEDE7] bg-white p-4 sm:p-5 space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                        <label className="co-label">Period</label>
                        <select value={form.period} onChange={(e) => set("period", e.target.value)}
                                className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-[14px]"
                                data-testid="be-period">
                            {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    {form.period === "custom" && (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="co-label">From</label>
                                <input type="date" value={form.custom_from} onChange={(e) => set("custom_from", e.target.value)}
                                       className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-[14px]" />
                            </div>
                            <div>
                                <label className="co-label">To</label>
                                <input type="date" value={form.custom_to} onChange={(e) => set("custom_to", e.target.value)}
                                       className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-[14px]" />
                            </div>
                        </div>
                    )}
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                        <label className="co-label">Target profit ($) <span className="text-[11px] text-[#9CA0A8]">optional</span></label>
                        <input type="number" step="100" value={form.target_profit}
                               onChange={(e) => set("target_profit", e.target.value)}
                               placeholder="0"
                               className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-[14px]"
                               data-testid="be-target-profit" />
                    </div>
                    <div>
                        <label className="co-label">Forecast sales ($) <span className="text-[11px] text-[#9CA0A8]">optional</span></label>
                        <input type="number" step="100" value={form.forecast_sales}
                               onChange={(e) => set("forecast_sales", e.target.value)}
                               placeholder="Auto"
                               className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-[14px]"
                               data-testid="be-forecast-sales" />
                    </div>
                    <div>
                        <label className="co-label">Avg $ per {form.unit_label || "customer"}</label>
                        <input type="number" step="1" value={form.avg_unit_value}
                               onChange={(e) => set("avg_unit_value", e.target.value)}
                               placeholder="e.g. 65"
                               className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-[14px]"
                               data-testid="be-avg-unit" />
                    </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                        <label className="co-label">Unit label</label>
                        <input value={form.unit_label} onChange={(e) => set("unit_label", e.target.value)}
                               placeholder="customer / job / booking / unit"
                               className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-[14px]"
                               data-testid="be-unit-label" />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                        <input type="checkbox" id="owner-wage" checked={form.include_owner_wage}
                               onChange={(e) => set("include_owner_wage", e.target.checked)}
                               data-testid="be-owner-wage-toggle" />
                        <label htmlFor="owner-wage" className="text-[13px]">Include fair owner wage</label>
                        {form.include_owner_wage && (
                            <input type="number" step="100" value={form.owner_wage_amount}
                                   onChange={(e) => set("owner_wage_amount", e.target.value)}
                                   placeholder="Per month ($)"
                                   className="ml-2 w-32 rounded-md border border-[#E4E4E7] bg-white px-2 py-1 text-[13px]"
                                   data-testid="be-owner-wage-amount" />
                        )}
                    </div>
                </div>

                <button onClick={compute} disabled={loading}
                        className="rounded-md bg-[#0F1115] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#2E2E2E] disabled:opacity-50"
                        data-testid="be-recalc">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Recalculate"}
                </button>
            </div>

            {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-800">
                    <AlertTriangle className="h-4 w-4 inline mr-1" /> {error}
                </div>
            )}

            {answer && (
                <>
                    {/* Primary answer */}
                    <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5 sm:p-6" data-testid="be-primary">
                        <div className="co-label text-emerald-900">The answer for {answer.period.label}</div>
                        <p className="mt-2 font-display text-xl sm:text-2xl font-semibold tracking-tight text-emerald-950 leading-tight">
                            {answer.primary_answer}
                        </p>
                        <div className={`mt-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-medium ${c.cls}`} data-testid="be-confidence">
                            <CheckCircle2 className="h-3.5 w-3.5" /> {c.label}
                        </div>
                    </div>

                    {/* Grid of the numbers that matter */}
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        <Kpi label="Gross profit required" value={nz(t.required_gross_profit)} />
                        <Kpi label="Break-even sales"      value={nz(t.break_even_sales)} />
                        <Kpi label="Required sales (target)" value={nz(t.required_sales)}
                             sub={answer.options.target_profit > 0 ? `Includes ${nz(answer.options.target_profit)} profit target` : "Same as break-even (no profit target)"} />
                        <Kpi label="Current / expected sales" value={nz(t.forecast_sales)}
                             sub={form.forecast_sales === "" ? "From trailing 3-month average" : "You entered this"} />
                        <Kpi label={t.sales_gap >= 0 ? "Additional sales needed" : "Buffer above break-even"}
                             value={nz(Math.abs(t.sales_gap || 0))}
                             tone={t.sales_gap >= 0 ? "amber" : "emerald"} />
                        <Kpi label="Forecast profit / (loss)" value={nz(t.forecast_operating_profit)}
                             tone={(t.forecast_operating_profit || 0) >= 0 ? "emerald" : "red"} />
                        <Kpi label="Daily sales target"    value={nz(t.daily_target)}
                             sub={`${answer.period.trading_days} trading days`} />
                        <Kpi label="Weekly sales target"   value={nz(t.weekly_target)}
                             sub={`${answer.period.trading_weeks} trading weeks`} />
                        <Kpi label={`${t.unit_label || "Customers"} required`}
                             value={t.units_required != null ? String(t.units_required) : "—"}
                             sub={t.units_required != null ? `at ${nz(t.avg_unit_value)} per ${t.unit_label}` : `Enter avg $ per ${form.unit_label}`} />
                        <Kpi label="Gross profit % used"   value={pct(answer.assumptions_basis.gp_pct)}
                             sub={`Cost of goods ${pct(answer.assumptions_basis.cogs_pct)}`} />
                        <Kpi label="Max variable cost %"   value={pct(t.max_variable_cost_pct)}
                             sub="Beyond this you'd miss break-even" />
                        <Kpi label="Fixed costs for period" value={nz(t.fixed_costs_period)}
                             sub={`Based on ${answer.period.months_span}-month rollout`} />
                    </div>

                    {/* Recommendation + warnings */}
                    <div className="rounded-2xl bg-[#F5F5F0] p-4 sm:p-5" data-testid="be-recommendation">
                        <div className="co-label text-[#5E636E]">Do this next</div>
                        <p className="mt-1.5 text-[15px] leading-relaxed text-[#0F1115]">{answer.recommendation}</p>
                    </div>

                    {(answer.warnings || []).length > 0 && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12.5px] text-amber-900" data-testid="be-warnings">
                            {answer.warnings.map((w, i) => <div key={i} className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /><span>{w}</span></div>)}
                        </div>
                    )}

                    {/* Show me how this was calculated */}
                    <button onClick={() => setShowMath(!showMath)}
                            className="inline-flex items-center gap-1 text-[13px] font-medium text-[#0F1115] hover:underline"
                            data-testid="be-toggle-math">
                        {showMath ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        Show me how this was calculated
                    </button>
                    {showMath && (
                        <div className="rounded-2xl border border-[#EEEDE7] bg-white p-4 sm:p-5 text-[13px] leading-relaxed space-y-3" data-testid="be-math">
                            <p><b>Data source:</b> {answer.assumptions_basis.source === "prosaic" ? "Prosaic" : answer.assumptions_basis.source === "xero" ? "Xero" : "No accounting connection"} · Trailing 3 months {answer.assumptions_basis.from} → {answer.assumptions_basis.to}.</p>
                            <table className="w-full">
                                <tbody>
                                    <Row label="Monthly revenue (avg)" v={nz(answer.assumptions_basis.monthly_revenue)} />
                                    <Row label="Monthly variable costs (avg)" v={nz(answer.assumptions_basis.monthly_variable)} />
                                    <Row label="Monthly fixed costs (avg)" v={nz(answer.assumptions_basis.monthly_fixed)} />
                                    {answer.assumptions_basis.mixed_accounts_amount > 0 &&
                                        <Row label="Of which mixed accounts (treated as fixed)" v={nz(answer.assumptions_basis.mixed_accounts_amount)} muted />}
                                    <Row label="Gross profit %" v={pct(answer.assumptions_basis.gp_pct)} bold />
                                    <Row label="× months in period" v={`× ${answer.period.months_span}`} muted />
                                    <Row label="= Fixed costs for period" v={nz(t.fixed_costs_period)} bold />
                                    {answer.options.target_profit > 0 && <Row label="+ Target operating profit" v={nz(answer.options.target_profit)} />}
                                    <Row label="= Gross profit required" v={nz(t.required_gross_profit)} bold />
                                    <Row label="÷ Gross profit %" v={pct(answer.assumptions_basis.gp_pct)} muted />
                                    <Row label="= Required sales" v={nz(t.required_sales)} bold highlight />
                                    <Row label="− Forecast sales" v={`(${nz(t.forecast_sales)})`} />
                                    <Row label={t.sales_gap >= 0 ? "= Additional sales needed" : "= Buffer above break-even"}
                                         v={nz(Math.abs(t.sales_gap || 0))} bold highlight={t.sales_gap >= 0} />
                                </tbody>
                            </table>
                        </div>
                    )}

                    <p className="text-[11px] text-[#5E636E] leading-relaxed">{answer.disclaimer}</p>
                </>
            )}
        </div>
    );
}

function Kpi({ label, value, sub, tone = "neutral" }) {
    const toneCls = {
        neutral: "border-[#EEEDE7] bg-white",
        emerald: "border-emerald-200 bg-emerald-50",
        amber:   "border-amber-200 bg-amber-50",
        red:     "border-red-200 bg-red-50",
    }[tone] || "border-[#EEEDE7] bg-white";
    return (
        <div className={`rounded-2xl border p-4 ${toneCls}`}>
            <div className="co-label">{label}</div>
            <div className="mt-1 font-display text-xl font-semibold tabular-nums text-[#0F1115]">{value}</div>
            {sub && <p className="mt-1 text-[11.5px] text-[#5E636E] leading-relaxed">{sub}</p>}
        </div>
    );
}

function Row({ label, v, bold, muted, highlight }) {
    return (
        <tr className={muted ? "text-[#5E636E]" : ""}>
            <td className={`py-1 ${bold ? "font-semibold text-[#0F1115]" : ""}`}>{label}</td>
            <td className={`py-1 text-right tabular-nums ${bold ? "font-semibold" : ""} ${highlight ? "text-emerald-800" : ""}`}>{v}</td>
        </tr>
    );
}
